<template>
  <div class="area-download p-4">
    <h5 class="bold mb-4">
      <i class="fas fa-download me-2"></i>
      Downloads
    </h5>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner-wrapper">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
        <p class="loading-text mt-3">Carregando documentos disponíveis...</p>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="!documentos.length" class="empty-state">
      <div class="empty-icon">
        <i class="fas fa-file-download"></i>
      </div>
      <h6 class="empty-title">Nenhum documento disponível</h6>
      <p class="empty-text">Não há documentos disponíveis para download no momento.</p>
      <small class="empty-hint">Os documentos serão disponibilizados pelos administradores.</small>
    </div>

    <!-- Lista de Documentos -->
    <div v-else>
      <!-- Filtro por Inscrição -->
      <div class="filter-section mb-4">
        <label class="filter-label">
          <i class="fas fa-filter"></i>
          Filtrar por Inscrição:
        </label>
        <select v-model="filtroInscricao" class="form-control form-select filter-select">
          <option value="">Todas as inscrições</option>
          <option v-for="inscricao in inscricoesUnicas" :key="inscricao.id" :value="inscricao.id">
            Inscrição #{{ inscricao.id }} - {{ inscricao.vaga?.cargo || 'N/A' }} ({{ inscricao.concurso?.prefeitura || 'N/A' }})
          </option>
        </select>
      </div>

      <!-- Tabela Desktop -->
      <div class="table-responsive d-none d-md-block">
        <table class="table table-striped table-hover">
          <thead class="table-dark">
            <tr>
              <th>Data</th>
              <th>Título</th>
              <th>Descrição</th>
              <th>Tipo</th>
              <th>Inscrição</th>
              <th class="text-center">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="documento in documentosFiltrados" :key="documento.id">
              <td>{{ formatarData(documento.created_at) }}</td>
              <td>
                <strong>{{ documento.titulo }}</strong>
              </td>
              <td>
                <span class="text-muted">{{ documento.descricao || '--' }}</span>
              </td>
              <td>
                <span class="badge bg-info">{{ formatarTipo(documento.tipo) }}</span>
              </td>
              <td>
                <span class="badge bg-primary">#{{ documento.inscricao_id }}</span>
                <br>
                <small class="text-muted">{{ documento.inscricao?.vaga?.cargo || '--' }}</small>
              </td>
              <td class="text-center">
                <button 
                  class="btn btn-primary btn-sm"
                  @click="baixarDocumento(documento)"
                  title="Baixar documento"
                >
                  <i class="fas fa-download"></i>
                  Download
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Cards Mobile -->
      <div class="mobile-cards d-md-none">
        <div v-for="documento in documentosFiltrados" :key="documento.id" class="mobile-card">
          <div class="card-header-mobile">
            <span class="badge bg-primary">#{{ documento.inscricao_id }}</span>
            <span class="badge bg-info">{{ formatarTipo(documento.tipo) }}</span>
          </div>
          <div class="card-body-mobile">
            <div class="card-item">
              <span class="label"><i class="fas fa-calendar"></i> Data:</span>
              <span class="value">{{ formatarData(documento.created_at) }}</span>
            </div>
            <div class="card-item">
              <span class="label"><i class="fas fa-heading"></i> Título:</span>
              <span class="value"><strong>{{ documento.titulo }}</strong></span>
            </div>
            <div class="card-item" v-if="documento.descricao">
              <span class="label"><i class="fas fa-align-left"></i> Descrição:</span>
              <span class="value">{{ documento.descricao }}</span>
            </div>
            <div class="card-item">
              <span class="label"><i class="fas fa-briefcase"></i> Cargo:</span>
              <span class="value">{{ documento.inscricao?.vaga?.cargo || '--' }}</span>
            </div>
            <div class="card-item">
              <span class="label"><i class="fas fa-building"></i> Prefeitura:</span>
              <span class="value">{{ documento.inscricao?.concurso?.prefeitura || '--' }}</span>
            </div>
          </div>
          <div class="card-footer-mobile">
            <button 
              class="btn btn-primary btn-sm btn-block-mobile"
              @click="baixarDocumento(documento)"
            >
              <i class="fas fa-download"></i> Baixar Documento
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'Download',
  
  data() {
    return {
      loading: false,
      documentos: [],
      filtroInscricao: ''
    }
  },
  
  created() {
    this.carregarDocumentos()
  },
  
  computed: {
    documentosFiltrados() {
      if (!this.filtroInscricao) {
        return this.documentos
      }
      return this.documentos.filter(doc => doc.inscricao_id == this.filtroInscricao)
    },
    
    inscricoesUnicas() {
      const inscricoesMap = new Map()
      this.documentos.forEach(doc => {
        if (doc.inscricao && !inscricoesMap.has(doc.inscricao.id)) {
          inscricoesMap.set(doc.inscricao.id, doc.inscricao)
        }
      })
      return Array.from(inscricoesMap.values())
    }
  },
  
  methods: {
    async carregarDocumentos() {
      try {
        this.loading = true
        const { data } = await axios.get('/ps/documento-inscricao')
        this.documentos = data || []
      } catch (error) {
        console.error('Erro ao carregar documentos:', error)
        this.documentos = []
        
        if (error.response?.status === 401) {
          this.$sweet.sweetMensagem("Sessão expirada. Faça login novamente.")
          this.$router.push({ name: 'home' })
        } else {
          this.$sweet.sweetMensagem("Erro ao carregar documentos. Tente novamente.")
        }
      } finally {
        this.loading = false
      }
    },
    
    baixarDocumento(documento) {
      if (!documento.caminho_arquivo) {
        this.$sweet.sweetMensagem("Arquivo não disponível para download.")
        return
      }
      
      // Abrir o link em nova aba para download
      window.open(documento.caminho_arquivo, '_blank')
    },
    
    formatarData(data) {
      if (!data) return "-"
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof data === 'string' && data.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = data.split('-')
          return `${day}/${month}/${year}`
        }
        
        // Tratar formato do banco: YYYY-MM-DD HH:mm:ss (com espaço)
        if (typeof data === 'string') {
          let dateString = data.replace('Z', '')
          
          // Se tiver formato com espaço (formato do banco: YYYY-MM-DD HH:mm:ss)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
            const [datePart] = dateString.split(' ')
            const [year, month, day] = datePart.split('-')
            return `${day}/${month}/${year}`
          }
          
          // Para datas ISO com timezone, remover o 'Z' e tratar como local
          if (dateString.includes('T')) {
            dateString = dateString.replace('Z', '')
            if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
              const [datePart] = dateString.split('T')
              const [year, month, day] = datePart.split('-')
              return `${day}/${month}/${year}`
            }
          }
        }
        
        // Criar data e usar métodos locais
        const dateObj = new Date(data)
        if (isNaN(dateObj.getTime())) return "-"
        
        const dia = String(dateObj.getDate()).padStart(2, '0')
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0')
        const ano = dateObj.getFullYear()
        return `${dia}/${mes}/${ano}`
      } catch (error) {
        return "-"
      }
    },
    
    formatarTipo(tipo) {
      if (!tipo) return 'Documento'
      
      const tipos = {
        'cartao_resposta': 'Cartão Resposta',
        'documento': 'Documento',
        'edital': 'Edital',
        'resultado': 'Resultado',
        'gabarito': 'Gabarito'
      }
      
      return tipos[tipo] || tipo
    }
  }
}
</script>

<style lang="sass" scoped>
.area-download
  background-color: #fff
  padding: 25px 15px
  border-radius: 10px
  box-shadow: 0 2px 10px rgba(0,0,0,0.1)
  
  h5
    font-size: 20px
    text-align: center
    color: #2c3e50
    margin-bottom: 30px
    font-weight: 600

// Loading States
.loading-container
  display: flex
  justify-content: center
  align-items: center
  padding: 60px 20px
  min-height: 200px

  .spinner-wrapper
    text-align: center

    .loading-text
      color: #6c757d
      font-size: 14px
      font-weight: 500
      margin-top: 15px

.spinner-border
  width: 3rem
  height: 3rem
  border-width: 0.3em

// Empty States
.empty-state
  text-align: center
  padding: 60px 30px
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-radius: 12px
  border: 2px dashed #dee2e6

  .empty-icon
    width: 80px
    height: 80px
    margin: 0 auto 20px
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%)
    border-radius: 50%
    display: flex
    align-items: center
    justify-content: center
    
    i
      font-size: 2.5rem
      color: white

  .empty-title
    font-size: 18px
    font-weight: 600
    color: #495057
    margin-bottom: 10px

  .empty-text
    font-size: 14px
    color: #6c757d
    margin-bottom: 8px
    line-height: 1.6

  .empty-hint
    font-size: 12px
    color: #adb5bd
    font-style: italic

// Filter Section
.filter-section
  background: #f8f9fa
  padding: 15px
  border-radius: 8px
  border: 1px solid #e9ecef

  .filter-label
    font-weight: 600
    color: #495057
    margin-bottom: 8px
    display: block
    font-size: 14px

    i
      margin-right: 8px
      color: #007bff

  .filter-select
    border: 2px solid #e9ecef
    border-radius: 6px
    padding: 8px 12px
    font-size: 14px
    transition: all 0.3s ease

    &:focus
      border-color: #007bff
      box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25)
      outline: none

// Table
.table-responsive
  border-radius: 10px
  overflow: hidden
  box-shadow: 0 2px 10px rgba(0,0,0,0.1)

  @media (max-width: 767px)
    display: none !important

.table
  margin-bottom: 0
  
  th
    font-weight: 600
    text-transform: uppercase
    font-size: 12px
    letter-spacing: 0.5px
  
  td
    vertical-align: middle
    padding: 15px 12px

.btn
  border-radius: 6px
  font-weight: 500
  transition: all 0.3s ease
  
  &:hover
    transform: translateY(-1px)
    box-shadow: 0 4px 8px rgba(0,0,0,0.15)

.badge
  font-size: 11px
  padding: 6px 10px
  border-radius: 20px
  font-weight: 600

// Mobile Cards
.mobile-cards
  display: flex
  flex-direction: column
  gap: 15px
  margin-top: 15px

  @media (min-width: 768px)
    display: none !important

.mobile-card
  background: white
  border-radius: 10px
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1)
  overflow: hidden
  border: 1px solid #e9ecef
  transition: all 0.3s ease

  &:hover
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15)
    transform: translateY(-2px)

.card-header-mobile
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  padding: 12px 15px
  display: flex
  justify-content: space-between
  align-items: center
  border-bottom: 2px solid #dee2e6

  .badge
    font-size: 12px
    padding: 6px 10px
    font-weight: 600

.card-body-mobile
  padding: 15px

.card-item
  display: flex
  align-items: flex-start
  padding: 10px 0
  border-bottom: 1px solid #f0f0f0
  flex-wrap: wrap
  gap: 8px

  &:last-child
    border-bottom: none

  .label
    font-weight: 600
    color: #495057
    font-size: 13px
    display: flex
    align-items: center
    gap: 6px
    min-width: 100px
    flex-shrink: 0

    i
      color: #007bff
      font-size: 14px
      width: 16px

  .value
    color: #6c757d
    font-size: 13px
    flex: 1
    overflow-wrap: break-word
    word-break: break-word

.card-footer-mobile
  padding: 15px
  background: #f8f9fa
  border-top: 1px solid #e9ecef

.btn-block-mobile
  width: 100%
  padding: 10px
  font-size: 13px
  font-weight: 500
  display: flex
  align-items: center
  justify-content: center
  gap: 8px

// Responsividade adicional para mobile
@media (max-width: 576px)
  .card-item
    flex-direction: column
    align-items: flex-start
    gap: 5px

    .label
      min-width: auto
      width: 100%

    .value
      width: 100%
      padding-left: 22px

  .card-header-mobile
    flex-direction: column
    gap: 8px
    align-items: flex-start

  .mobile-card
    margin-bottom: 10px

  .area-download
    padding: 15px !important
</style>
